import React, { useState, useEffect, useRef } from 'react';
import { Mail, ShieldCheck, RefreshCw } from 'lucide-react';
import Button from '../../components/Button';
import Card from '../../components/Card';

/**
 * Which flow this screen is serving. `signup` is the original path (confirming a new account's email);
 * `login` is signing in with an emailed code.
 *
 * It exists because `Login.tsx` routed the CALLBACKS through `codePurpose` but the static copy was never made
 * purpose-aware — so a returning user signing in read "Verify Your Email" and "← Back to signup" when no
 * signup was involved. Defaults to `signup`, so every existing caller renders exactly as before.
 */
type CodePurpose = 'signup' | 'login';

const DEFAULT_RESEND_COOLDOWN_SECONDS = 60;

interface OtpVerificationProps {
  email: string;
  purpose?: CodePurpose;
  onVerify: (otpCode: string) => Promise<void>;
  /** May resolve to a cooldown in seconds (the server's retry_after); otherwise the default applies. */
  onResend: () => Promise<void | number>;
  onBack: () => void;
  isLoading: boolean;
  error: string | null;
}

const COPY: Record<
  CodePurpose,
  {
    groupLabel: string;
    digit: string;
    heading: string;
    lead: string;
    submit: string;
    back: string;
  }
> = {
  signup: {
    groupLabel: 'Enter verification code',
    digit: 'Verification code digit',
    heading: 'Verify Your Email',
    lead: "We've sent a verification code to",
    submit: 'Verify Email',
    back: '← Back to signup',
  },
  login: {
    groupLabel: 'Enter your sign-in code',
    digit: 'Sign-in code digit',
    heading: 'Enter your sign-in code',
    lead: "We've sent a sign-in code to",
    submit: 'Sign in',
    back: '← Use a password instead',
  },
};

const OtpVerification: React.FC<OtpVerificationProps> = ({
  email,
  purpose = 'signup',
  onVerify,
  onResend,
  onBack,
  isLoading,
  error,
}) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Handle resend cooldown
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) {
      // Handle paste
      const pastedCode = value.replace(/\D/g, '').slice(0, 6).split('');
      const newOtp = [...otp];
      pastedCode.forEach((digit, i) => {
        if (i < 6) newOtp[i] = digit;
      });
      setOtp(newOtp);

      // Focus last filled input or next empty one
      const lastFilledIndex = Math.min(pastedCode.length - 1, 5);
      inputRefs.current[lastFilledIndex]?.focus();

      // Auto-submit if all 6 digits are pasted
      if (pastedCode.length === 6) {
        handleSubmit(newOtp.join(''));
      }
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits are entered
    if (value && index === 5 && newOtp.every((digit) => digit)) {
      handleSubmit(newOtp.join(''));
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const pastedCode = pastedData.replace(/\D/g, '').slice(0, 6).split('');

    if (pastedCode.length > 0) {
      const newOtp = [...otp];
      pastedCode.forEach((digit, i) => {
        if (i < 6) newOtp[i] = digit;
      });
      setOtp(newOtp);

      // Focus the last filled input or the next empty one
      const lastFilledIndex = Math.min(pastedCode.length - 1, 5);
      setTimeout(() => {
        inputRefs.current[lastFilledIndex]?.focus();
      }, 0);

      // Auto-submit if all 6 digits are pasted
      if (pastedCode.length === 6) {
        handleSubmit(newOtp.join(''));
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async (otpCode?: string) => {
    const code = otpCode || otp.join('');
    if (code.length === 6) {
      await onVerify(code);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    try {
      const wait = await onResend();
      setResendCooldown(
        typeof wait === 'number' && wait > 0 ? wait : DEFAULT_RESEND_COOLDOWN_SECONDS,
      );
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto p-8">
      <div className="text-center mb-8">
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-blue-100 rounded-full">
            <ShieldCheck className="h-8 w-8 text-blue-600" />
          </div>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">{COPY[purpose].heading}</h1>
        <p className="text-gray-600">{COPY[purpose].lead}</p>
        <p className="text-sm font-medium text-gray-900 mt-1 flex items-center justify-center">
          <Mail className="h-4 w-4 mr-1" />
          {email}
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              {COPY[purpose].groupLabel}
            </label>
            <div className="flex justify-center space-x-2">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value.replace(/\D/g, ''))}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  // Each box needs its own accessible name: the visible label describes the GROUP, and a
                  // label with no associated control is announced to nobody. Without this a screen-reader
                  // user hears six anonymous textboxes and cannot tell which digit they are on.
                  aria-label={`${COPY[purpose].digit} ${index + 1} of ${otp.length}`}
                  autoComplete={index === 0 ? 'one-time-code' : 'off'}
                  className="w-12 h-12 text-center text-lg font-semibold border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-all"
                  disabled={isLoading}
                />
              ))}
            </div>
            <p className="mt-2 text-xs text-gray-500 text-center">
              Enter the 6-digit code from your email
            </p>
          </div>

          <Button type="submit" fullWidth disabled={isLoading || otp.some((digit) => !digit)}>
            {isLoading ? (
              <>
                <span className="inline-block h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                Verifying...
              </>
            ) : (
              COPY[purpose].submit
            )}
          </Button>

          <div className="text-center space-y-4">
            <div>
              <p className="text-sm text-gray-600 mb-2">Didn't receive the code?</p>
              <button
                type="button"
                onClick={handleResend}
                disabled={resendLoading || resendCooldown > 0}
                className="font-medium text-blue-600 hover:text-blue-500 disabled:text-gray-400 disabled:cursor-not-allowed inline-flex items-center"
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${resendLoading ? 'animate-spin' : ''}`} />
                {resendCooldown > 0
                  ? `Resend in ${resendCooldown}s`
                  : resendLoading
                    ? 'Sending...'
                    : 'Resend code'}
              </button>
            </div>

            <button
              type="button"
              onClick={onBack}
              className="text-sm text-gray-600 hover:text-gray-800"
            >
              {COPY[purpose].back}
            </button>
          </div>
        </div>
      </form>
    </Card>
  );
};

export default OtpVerification;
